import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

const GHOST_ICONS_RENDER_DELAY_MS = 300;

// How long to wait after ghost icons become renderable before deciding that
// we are still blocked on source-icon image loads. Set well above the 300ms
// coordination delay so a normal slow image load does not trip it, but low
// enough that a stuck `<Image>` (no `onLoad` and no `onError`) shows up in
// dashboards within a single user interaction window.
const GHOST_ICONS_RENDER_BLOCKED_THRESHOLD_MS = 2000;

type AppLike = {
	source: string;
};

// Reasons surfaced via `onRenderBlocked` so callers can attach a single
// `ghost_icons_render_blocked` operational event with the right `reason`
// attribute. Today there is only one reason ('awaiting_source_icons') but the
// shape is extensible so additional coordination-level stalls can be reported
// without churning the callback signature.
export type GhostIconsRenderBlockedReason = 'awaiting_source_icons';

export type GhostIconsRenderBlockedDetail = {
	reason: GhostIconsRenderBlockedReason;
	// Sources of the visible app icons we are still waiting on. Bounded by
	// `maxAppIconsVisible` so the payload stays small for analytics.
	pendingSources: string[];
};

type UseGhostRenderCoordinationArgs = {
	filteredApps: AppLike[];
	webSearchEnabled: boolean;
	webSearchConfigEnabled: boolean;
	// Ghost feature is eligible and its connector data is ready to render.
	shouldRenderGhostIcons: boolean;
	// Ghost feature is eligible but its connector data is still loading.
	isGhostIconsPending: boolean;
	// Number of ghost icons available to render.
	ghostIconCount: number;
	// Fired once per render-eligibility window when ghost icons are ready but
	// the coordination layer is still waiting on source-icon image loads past
	// `GHOST_ICONS_RENDER_BLOCKED_THRESHOLD_MS`. Lets `useGhostIconsPillState`
	// emit a `ghost_icons_render_blocked` operational event so we can detect
	// silent suppression caused by slow/missing BE icon URLs (which would
	// otherwise look identical to "user has no eligible connectors" in
	// analytics).
	onRenderBlocked?: (detail: GhostIconsRenderBlockedDetail) => void;
};

// Consolidated view of how the ghost icon row should render, so consumers only
// reason about a single object instead of several overlapping booleans.
type GhostIconRowState = {
	// Ghost icons are coordinated (source icons loaded + delay elapsed) and present,
	// so the wave should render now.
	isVisible: boolean;
	// Ghost icons are pending or already rendering, so the "+N" overflow count must
	// stay hidden — otherwise it flashes in during the fetch/delay and is then
	// replaced by the ghost icon wave.
	shouldHideOverflowCount: boolean;
};

type UseGhostRenderCoordinationResult = {
	ghostIconRow: GhostIconRowState;
	markSourceIconLoaded: (source: string) => void;
};

export const useGhostRenderCoordination = ({
	filteredApps,
	webSearchEnabled,
	webSearchConfigEnabled,
	shouldRenderGhostIcons,
	isGhostIconsPending,
	ghostIconCount,
	onRenderBlocked,
}: UseGhostRenderCoordinationArgs): UseGhostRenderCoordinationResult => {
	// Tracks which visible 1P source icons have finished loading so ghost icons
	// can appear in sync (avoids a jarring staggered render).
	const [loadedSourceIconsBySource, setLoadedSourceIconsBySource] = useState<Record<string, true>>(
		{},
	);

	// Idempotently marks a source icon as loaded from either onLoad or onError,
	// so one missing icon does not block ghost icon rendering forever.
	const markSourceIconLoaded = useCallback((source: string) => {
		setLoadedSourceIconsBySource((previous) =>
			previous[source] ? previous : { ...previous, [source]: true },
		);
	}, []);

	// Cap the number of icons we gate on — and report — at the number of icons
	// the pill will actually render. Hoisted so both the load-gate and the
	// `onRenderBlocked` payload agree on the same set.
	const visibleAppSources = useMemo(() => {
		const maxVisible = 3;
		const maxAppIconsVisible =
			webSearchEnabled && webSearchConfigEnabled ? maxVisible - 1 : maxVisible;
		return filteredApps.slice(0, maxAppIconsVisible);
	}, [filteredApps, webSearchEnabled, webSearchConfigEnabled]);

	const hasLoadedAppSourceIcons = useMemo(() => {
		if (filteredApps.length === 0) {
			// No 1P icons to wait for — allow ghost icons to render.
			return true;
		}
		if (!fg('rovo_chat_use_be_knowledge_icons')) {
			// Without BE icon URLs we render logo fallbacks immediately, so there is
			// no async image load gate to wait on.
			return true;
		}
		// Only gate on the icons that are actually visible in the pill. When web
		// is shown, it consumes one slot, so one fewer app icon needs loading.
		return visibleAppSources.every((app) => loadedSourceIconsBySource[app.source]);
	}, [filteredApps, loadedSourceIconsBySource, visibleAppSources]);

	// Adds a short delay before showing ghost icons so they animate in only after
	// the source icon row is stable.
	const [isCoordinatedRenderReady, setIsCoordinatedRenderReady] = useState(false);
	useEffect(() => {
		if (!shouldRenderGhostIcons || !hasLoadedAppSourceIcons) {
			// Reset immediately when ghost rendering is disabled or we are still
			// waiting for required source icons.
			setIsCoordinatedRenderReady(false);
			return;
		}
		const timeoutId = globalThis.setTimeout(
			() => setIsCoordinatedRenderReady(true),
			GHOST_ICONS_RENDER_DELAY_MS,
		);
		return () => globalThis.clearTimeout(timeoutId);
	}, [shouldRenderGhostIcons, hasLoadedAppSourceIcons]);

	// "Render blocked" detection: when ghost icons are ready to render but the
	// source-icon load gate has been false for longer than the threshold, fire
	// a single notification per eligibility window so callers can emit an
	// operational analytics event. Reference held in a ref so identity churn
	// on the callback does not retrigger the timer.
	const onRenderBlockedRef = useRef(onRenderBlocked);
	useEffect(() => {
		onRenderBlockedRef.current = onRenderBlocked;
	}, [onRenderBlocked]);
	const renderBlockedFiredRef = useRef(false);
	useEffect(() => {
		// Reset the dedupe latch whenever ghost rendering is not eligible OR the
		// load gate has opened — both make the "blocked" state no longer apply
		// and any future stall should re-fire.
		if (!shouldRenderGhostIcons || hasLoadedAppSourceIcons) {
			renderBlockedFiredRef.current = false;
			return;
		}
		if (renderBlockedFiredRef.current) {
			return;
		}
		const timeoutId = globalThis.setTimeout(() => {
			renderBlockedFiredRef.current = true;
			onRenderBlockedRef.current?.({
				reason: 'awaiting_source_icons',
				pendingSources: visibleAppSources
					.filter((app) => !loadedSourceIconsBySource[app.source])
					.map((app) => app.source),
			});
		}, GHOST_ICONS_RENDER_BLOCKED_THRESHOLD_MS);
		return () => globalThis.clearTimeout(timeoutId);
	}, [
		shouldRenderGhostIcons,
		hasLoadedAppSourceIcons,
		visibleAppSources,
		loadedSourceIconsBySource,
	]);

	const ghostIconRow = useMemo<GhostIconRowState>(
		() => ({
			isVisible: isCoordinatedRenderReady && ghostIconCount > 0,
			// Hide the overflow count from the moment ghost icons are pending, through
			// the coordination delay, until the wave is on screen.
			shouldHideOverflowCount:
				isGhostIconsPending || (shouldRenderGhostIcons && ghostIconCount > 0),
		}),
		[isCoordinatedRenderReady, ghostIconCount, isGhostIconsPending, shouldRenderGhostIcons],
	);

	return {
		ghostIconRow,
		markSourceIconLoaded,
	};
};
